import { BarChart3, Boxes, Upload } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { cn } from '../lib/cn.js';
import { ImportTab } from '../features/assets/ImportTab.js';
import { OverviewTab } from '../features/assets/OverviewTab.js';
import { RegisterTab } from '../features/assets/RegisterTab.js';

type Tab = 'overview' | 'register' | 'import';

/**
 * Utstyr og lisenser (ASSETS module, L1). This round is the superadmin surface:
 * register, overview and import. The employee "Mine ting" view arrives with SSO
 * in L3, so a non-superadmin sees a short note rather than an empty page.
 */
export function AssetsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');

  if (!user?.isSuperadmin) {
    return (
      <div className="space-y-4">
        <Header />
        <GlassPanel>
          <EmptyState
            title="Din egen oversikt kommer snart."
            description="«Mine ting» — dine lisenser og ditt utstyr — åpnes når innlogging for alle er på plass. Foreløpig er det superadmin som bygger opp registeret."
          />
        </GlassPanel>
      </div>
    );
  }

  const tabs: { key: Tab; label: string; Icon: typeof BarChart3 }[] = [
    { key: 'overview', label: 'Oversikt', Icon: BarChart3 },
    { key: 'register', label: 'Register', Icon: Boxes },
    { key: 'import', label: 'Import', Icon: Upload },
  ];

  return (
    <div className="space-y-4">
      <Header />
      <nav className="border-hairline flex gap-1 border-b">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors',
              tab === key
                ? 'border-accent text-accent'
                : 'text-ink-soft hover:text-ink border-transparent',
            )}
          >
            <Icon size={15} aria-hidden />
            {label}
          </button>
        ))}
      </nav>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'register' && <RegisterTab />}
      {tab === 'import' && <ImportTab />}
    </div>
  );
}

function Header() {
  return (
    <header>
      <h1 className="font-display text-ink text-xl">Utstyr og lisenser</h1>
      <p className="text-ink-soft mt-0.5 text-sm">
        Registeret over programvarelisenser og utstyr — med summer, «sist fått» og fornyelseskalender.
      </p>
    </header>
  );
}
